import {dispatch, EVENT_ID, subscribe, unsubscribe} from '../../../../containers/event_manager';

// Simulated (design doc, Option A): a small client-side list, not the real PersonNotification
// system. No backend write, no socket. Session-only — does not persist across a reload. Replace
// with a real backend-driven surface once a run service gives Nova runs a real backend event to
// notify from.

export type NovaNotificationKind = 'succeeded' | 'failed';

export interface NovaNotification {
	id: string;
	taskId: string;
	taskName: string;
	agentName: string;
	kind: NovaNotificationKind;
	createdAt: number;
	// Absent when the caller has no project context to build one from (matches taskName's own
	// "cheaper to thread than re-derive" reasoning) — a consumer with no URL falls back to
	// whatever local, same-page open behavior it already has.
	taskUrl?: string;
	// The human-readable task id shown in the notification text (e.g. "T58785") — distinct from
	// `taskId`, which is the internal Task node id used for click/dismiss/viewed-tracking and is
	// not fit to show a user. Same optionality/reasoning as taskUrl above.
	companyTaskId?: string | number;
	// For the project-code pill, matching the real notification row's own project indicator.
	// Same optionality reasoning as taskUrl/companyTaskId above.
	projectColor?: string;
	companyProjectId?: string | number;
}

export interface NovaNotificationOverflowSummary {
	id: string;
	kind: 'overflow_summary';
	count: number;
	createdAt: number;
}

export type NovaNotificationEntry = NovaNotification | NovaNotificationOverflowSummary;

export const NOTIFICATIONS_PER_HOUR_CAP = 10;
const HOUR_MS = 60 * 60 * 1000;

let entries: NovaNotificationEntry[] = [];
let idCounter = 0;

function nextId(): string {
	idCounter += 1;
	return `nova-notif-${idCounter}`;
}

export function addNotification(
	input: {
		taskId: string;
		taskName: string;
		agentName: string;
		kind: NovaNotificationKind;
		taskUrl?: string;
		companyTaskId?: string | number;
		projectColor?: string;
		companyProjectId?: string | number;
	},
	now: number = Date.now()
): void {
	const withinLastHour = entries.filter(
		(e): e is NovaNotification => e.kind !== 'overflow_summary' && now - e.createdAt < HOUR_MS
	);
	// Reassigns rather than mutating in place (push, index-assignment) throughout — getNotifications()
	// hands this same reference to React consumers via setState, and a same-reference mutation
	// would look like "no change" to Object.is and silently skip their re-render.
	if (withinLastHour.length >= NOTIFICATIONS_PER_HOUR_CAP) {
		const summaryIndex = entries.findIndex(e => e.kind === 'overflow_summary');
		if (summaryIndex >= 0) {
			const summary = entries[summaryIndex] as NovaNotificationOverflowSummary;
			entries = entries.map((e, i) => (i === summaryIndex ? {...summary, count: summary.count + 1} : e));
		} else {
			entries = [...entries, {id: nextId(), kind: 'overflow_summary', count: 1, createdAt: now}];
		}
	} else {
		entries = [...entries, {id: nextId(), ...input, createdAt: now}];
	}
	dispatch(EVENT_ID.NOVA_NOTIFICATIONS_CHANGED, undefined);
}

export function getNotifications(): NovaNotificationEntry[] {
	return entries;
}

export function clearNotification(id: string): void {
	entries = entries.filter(e => e.id !== id);
	dispatch(EVENT_ID.NOVA_NOTIFICATIONS_CHANGED, undefined);
}

export function clearAllNotifications(): void {
	entries = [];
	dispatch(EVENT_ID.NOVA_NOTIFICATIONS_CHANGED, undefined);
}

export function subscribeToNotifications(callback: () => void): () => void {
	const handler = (): void => callback();
	subscribe<void>(EVENT_ID.NOVA_NOTIFICATIONS_CHANGED, handler);
	return () => unsubscribe<void>(EVENT_ID.NOVA_NOTIFICATIONS_CHANGED, handler);
}

// Seam for "is the user currently viewing this task" — no real caller yet (see the plan's Global
// Constraints). Defaults to false: notifications fire liberally until something real narrows it.
const viewedTaskIds = new Set<string>();
export function markTaskViewed(taskId: string): void {
	viewedTaskIds.add(taskId);
}
export function markTaskNotViewed(taskId: string): void {
	viewedTaskIds.delete(taskId);
}
export function isTaskCurrentlyViewed(taskId: string): boolean {
	return viewedTaskIds.has(taskId);
}
